import { useMemo, useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  CheckCircle2, Loader2, ShieldCheck, ArrowRight, Send, Check,
  MessagesSquare, Handshake, TrendingUp, Megaphone, GraduationCap, ArrowDown,
} from "lucide-react";
import { ASSETS } from "@/lib/constants";
import { track, EVENTS } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NO_REFERRER = "No one — I found this on my own";

/** Answer-value catalog. Values sent to the API stay stable; labels can change freely. */
const OPTS = {
  q1_years: [
    ["<1", "Less than 1 year"],
    ["1-3", "1–3 years"],
    ["3-5", "3–5 years"],
    ["5-10", "5–10 years"],
    ["10+", "More than 10 years"],
  ],
  q1_txn: [
    ["0", "0"],
    ["1-2", "1–2"],
    ["3-5", "3–5"],
    ["6-11", "6–11"],
    ["12-20", "12–20"],
    ["21+", "21+"],
  ],
  q2_business: [
    ["sphere_referral", "Sphere / referral-based"],
    ["team_generated", "Team-generated"],
    ["internet_leads", "Internet / paid leads"],
    ["open_houses", "Open houses / prospecting"],
    ["balanced", "Mixed / balanced"],
    ["still_building", "Still building foundation"],
  ],
  q3_sphere: [
    ["<75", "Under 75"],
    ["75-149", "75–149"],
    ["150-299", "150–299"],
    ["300-499", "300–499"],
    ["500-999", "500–999"],
    ["1000+", "1,000+"],
  ],
  q4_crm: [
    ["daily", "Daily / highly consistent"],
    ["weekly", "Weekly"],
    ["occasionally", "Occasionally"],
    ["rarely", "Rarely"],
    ["not_used", "Not currently using one"],
  ],
  q5_outreach: [
    ["weekly", "Weekly"],
    ["2-3_monthly", "2–3x per month"],
    ["monthly", "Monthly"],
    ["rarely", "Rarely"],
    ["not_consistent", "Not currently consistent"],
  ],
  q6_conversations: [
    ["<10", "Under 10"],
    ["10-19", "10–19"],
    ["20-29", "20–29"],
    ["30-49", "30–49"],
    ["50+", "50+"],
  ],
  q7_discipline: [
    ["extremely", "Extremely consistent"],
    ["very", "Very consistent"],
    ["somewhat", "Somewhat consistent"],
    ["inconsistent", "Inconsistent"],
    ["not_structured", "Not yet structured"],
  ],
  q8_coach: [
    ["very_high", "Very high"],
    ["high", "High"],
    ["moderate", "Moderate"],
    ["low", "Low"],
  ],
  q9_approach: [
    ["fully_integrated", "Fully integrated into daily outreach"],
    ["consistently_used", "Used consistently in relevant conversations"],
    ["campaigns", "Used with campaigns + proactive outreach"],
    ["opportunistic", "Occasional / opportunistic use only"],
    ["unsure", "Unsure / need guidance"],
  ],
};

// The 10 Core Commitments — required participation standards
const CORE_COMMITMENTS = [
  ["cc_1", "Active Participation", "I understand this is an active business development role requiring consistent engagement, not passive participation."],
  ["cc_2", "Onboarding & Training", "I agree to complete required onboarding, training, and applicable compliance education."],
  ["cc_3", "Approved Messaging & Systems", "I agree to use only approved Great Rate Check messaging, marketing materials, and referral/handoff processes."],
  ["cc_4", "Licensed Mortgage Referral Compliance", "I understand that all mortgage-related discussions requiring licensing will be referred to a properly licensed mortgage professional."],
  ["cc_5", "Professional Communication Standard", "I agree to maintain timely, professional communication with Great Rate Check and participating mortgage partners."],
  ["cc_6", "Outreach Expectation Commitment", "I agree to make a consistent effort to generate conversations through my sphere, database, and professional relationships."],
  ["cc_7", "Activity Tracking & Transparency", "I agree to maintain reasonable records of my outreach and provide activity updates when requested."],
  ["cc_8", "Meeting & Coaching Participation", "I agree to attend required Partner meetings, training sessions, and coaching calls."],
  ["cc_9", "90-Day Activation Commitment", "If accepted, I agree to complete the 90-day activation expectations, including CRM/database setup or optimization, consistent outreach activity, participation in training, and a target of 60–90 intentional conversations."],
  ["cc_10", "Performance-Based Participation", "I understand that continued participation, access, and support are based on activity, professionalism, responsiveness, and adherence to program standards, and may be modified or discontinued if expectations are not met."],
];

/* ---------- Small building blocks ---------- */
const Section = ({ number, title, subtitle, children }) => (
  <section className="rounded-3xl border border-[#EDE9DC] bg-white shadow-soft overflow-hidden">
    <div className="px-6 sm:px-8 py-5 border-b border-[#EDE9DC] bg-[#FDFBF7]">
      <div className="text-[11px] tracking-[0.18em] uppercase text-[#1F8F8B]">Section {number}</div>
      <h2 className="mt-1 text-xl font-semibold text-[#0F2A47]">{title}</h2>
      {subtitle ? <p className="mt-1 text-sm text-[#59564C]">{subtitle}</p> : null}
    </div>
    <div className="px-6 sm:px-8 py-6 space-y-6">{children}</div>
  </section>
);

const NumberedQ = ({ number, title, hint, children }) => (
  <div>
    <div className="flex items-start gap-3">
      <div className="w-7 h-7 shrink-0 rounded-full bg-[#0F2A47] text-white text-xs font-semibold flex items-center justify-center">{number}</div>
      <div className="flex-1">
        <h3 className="text-sm font-semibold text-[#0F2A47]">{title}</h3>
        {hint ? <p className="text-xs text-[#7A7668] mt-0.5">{hint}</p> : null}
      </div>
    </div>
    <div className="mt-3 pl-10">{children}</div>
  </div>
);

const Field = ({ label, hint, children, required }) => (
  <label className="block">
    <span className="block text-sm font-medium text-[#0F2A47]">
      {label} {required && <span className="text-[#B04A2E]">*</span>}
    </span>
    {hint ? <span className="block text-xs text-[#7A7668] mt-0.5">{hint}</span> : null}
    <div className="mt-1.5">{children}</div>
  </label>
);

const TextInput = (props) => (
  <input
    {...props}
    className="w-full rounded-xl border border-[#E6E2D6] focus:border-[#1F8F8B] focus:outline-none focus:ring-2 focus:ring-[#1F8F8B]/20 p-3 text-sm text-[#0F2A47] bg-white"
  />
);

const Select = ({ value, onChange, options, testid }) => (
  <select
    value={value}
    onChange={(e) => onChange(e.target.value)}
    data-testid={testid}
    className="w-full rounded-xl border border-[#E6E2D6] focus:border-[#1F8F8B] focus:outline-none focus:ring-2 focus:ring-[#1F8F8B]/20 p-3 text-sm text-[#0F2A47] bg-white"
  >
    <option value="">Select…</option>
    {options.map(([val, label]) => (
      <option key={val} value={val}>{label}</option>
    ))}
  </select>
);

function RadioGroup({ name, value, onChange, options, testid }) {
  return (
    <div className="space-y-2" role="radiogroup" data-testid={testid}>
      {options.map(([val, label]) => (
        <label
          key={val}
          className={`flex items-start gap-3 rounded-xl border p-3 cursor-pointer transition ${
            value === val ? "border-[#1F8F8B] bg-[#1F8F8B]/5" : "border-[#E6E2D6] hover:border-[#1F8F8B]/50"
          }`}
        >
          <input
            type="radio"
            name={name}
            checked={value === val}
            onChange={() => onChange(val)}
            className="mt-0.5 accent-[#1F8F8B]"
          />
          <span className="text-sm text-[#0F2A47]">{label}</span>
        </label>
      ))}
    </div>
  );
}

function CommitmentList({ items, values, onChange, testid }) {
  const toggle = (val) => {
    const next = values.includes(val)
      ? values.filter((v) => v !== val)
      : [...values, val];
    onChange(next);
  };
  return (
    <div className="space-y-2" data-testid={testid}>
      {items.map(([val, title, description], idx) => (
        <label
          key={val}
          className={`flex items-start gap-3 rounded-xl border p-3 sm:p-4 cursor-pointer transition ${
            values.includes(val) ? "border-[#1F8F8B] bg-[#1F8F8B]/5" : "border-[#E6E2D6] hover:border-[#1F8F8B]/50"
          }`}
        >
          <input
            type="checkbox"
            checked={values.includes(val)}
            onChange={() => toggle(val)}
            className="mt-1 accent-[#1F8F8B]"
          />
          <div className="flex-1">
            <div className="text-sm font-semibold text-[#0F2A47]">
              <span className="text-[#1F8F8B] mr-1">{idx + 1}.</span> {title}
            </div>
            <div className="text-sm text-[#59564C] mt-0.5 leading-relaxed">{description}</div>
            <div className="text-[11px] uppercase tracking-widest text-[#1F8F8B] mt-1.5">
              {values.includes(val) ? "✓ Agreed" : "☐ I Agree"}
            </div>
          </div>
        </label>
      ))}
    </div>
  );
}

/* ---------- Main page ---------- */
export default function BDMApply() {
  const { slug } = useParams();
  const referringSlug = (slug || "").trim().toLowerCase() || null;
  const brokerageCode = useMemo(
    () => (new URLSearchParams(window.location.search).get("b") || "").trim().toUpperCase(),
    [],
  );
  const [referringMlo, setReferringMlo] = useState(null);
  const [f, setF] = useState({
    full_name: "",
    email: "",
    mobile_phone: "",
    company: "",
    city_state: "",
    real_estate_license: "",
    grc_affiliation: "",
    grc_affiliation_with: "",
    referred_by: "",
    // 9 signals — kept using original backend field names for compat
    q1_years_in_re: "",
    q2_transactions_12mo: "",
    q4_business_type: "",
    q5_sphere_size: "",
    q7_crm_frequency: "",
    q9_outreach_frequency: "",
    q10_conversations_per_month: "",
    q12_consistency: "",
    q20_coachability: "",
    q14_approach: "",
    // Consolidated 10 core commitments → stored in agreements_section7
    agreements_section7: [],
    // Signature
    applicant_name: "",
    signature: "",
    signed_date: new Date().toISOString().slice(0, 10),
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  // If we're on /:slug/partnerapply, fetch the referring MLO's public profile
  // to pre-fill "Referred by" + show a small credit banner.
  useEffect(() => {
    if (!referringSlug) return;
    axios
      .get(`${API}/mlos/${encodeURIComponent(referringSlug)}/public`)
      .then((r) => {
        setReferringMlo(r.data);
        setF((prev) => ({
          ...prev,
          referred_by: prev.referred_by || r.data.name || "",
        }));
      })
      .catch(() => setReferringMlo(null));
  }, [referringSlug]);

  const set = (key, value) => setF((prev) => ({ ...prev, [key]: value }));

  const canSubmit = useMemo(
    () =>
      f.full_name.trim() &&
      f.email.trim() &&
      f.mobile_phone.trim() &&
      f.referred_by.trim() &&
      f.applicant_name.trim() &&
      f.signature.trim(),
    [f],
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setErr("");
    try {
      await axios.post(`${API}/bdm-applications`, {
        ...f,
        referring_mlo_slug: referringSlug || null,
        brokerage_code: brokerageCode || null,
      });
      track(EVENTS.PARTNER_APPLY_SUBMIT, { referring_mlo_slug: referringSlug || "direct" });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e2) {
      setErr(
        e2?.response?.data?.detail
          ? typeof e2.response.data.detail === "string"
            ? e2.response.data.detail
            : "Please check the form and try again."
          : "Something went wrong. Please retry.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div
        className="min-h-screen flex items-center justify-center px-6 py-16"
        style={{ background: "radial-gradient(1200px 600px at 20% -10%, #17385e 0%, #0F2A47 40%, #08192F 100%)" }}
      >
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-lg w-full bg-white rounded-3xl shadow-2xl p-8 text-center"
          data-testid="apply-success"
        >
          <div className="mx-auto w-14 h-14 rounded-full bg-[#A6D24A]/20 flex items-center justify-center">
            <CheckCircle2 className="w-8 h-8 text-[#1F8F8B]" />
          </div>
          <h1 className="mt-4 text-2xl font-semibold text-[#0F2A47]">Application received</h1>
          <p className="mt-2 text-sm text-[#59564C] leading-relaxed">
            Thank you for applying to the Great Rate Check Partner Program. Our team will review your
            application and follow up with next steps. If you have questions, reply to the confirmation
            email or reach out to your Great Rate Check contact directly.
          </p>
          <a href="/" className="mt-6 inline-block rounded-xl bg-[#0F2A47] hover:bg-[#173a5f] text-white px-5 py-2.5 text-sm">
            Return to Great Rate Check
          </a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="bdm-apply-page">
      <header
        className="relative text-white overflow-hidden"
        style={{ background: "linear-gradient(150deg, #08192F 0%, #0F2A47 45%, #123457 75%, #17385e 100%)" }}
      >
        {/* ambient glows */}
        <div className="pointer-events-none absolute -top-32 -right-24 w-[480px] h-[480px] rounded-full opacity-[0.16]" style={{ background: "radial-gradient(circle, #1F8F8B 0%, transparent 65%)" }} />
        <div className="pointer-events-none absolute -bottom-40 -left-32 w-[520px] h-[520px] rounded-full opacity-[0.10]" style={{ background: "radial-gradient(circle, #A6D24A 0%, transparent 65%)" }} />

        <div className="relative max-w-4xl mx-auto px-6 pt-10 pb-12 sm:pt-14 sm:pb-16">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <a href="/" className="inline-flex bg-white rounded-2xl px-3 py-1.5 shadow-soft">
              <img src={ASSETS.logoHorizontal} alt="Great Rate Check" className="h-9 sm:h-11 w-auto" />
            </a>
            <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.06] px-4 py-1.5 text-[11px] uppercase tracking-[0.18em] text-[#A6D24A]" data-testid="hero-badge">
              Partner Program · Application
            </span>
          </div>

          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="mt-9 text-4xl sm:text-5xl font-semibold tracking-tight leading-[1.08] max-w-2xl" data-testid="hero-headline">
              Your conversations are worth{" "}
              <span className="text-[#A6D24A]">more than you think.</span>
            </h1>
            <p className="mt-4 text-white/85 text-base sm:text-lg leading-relaxed max-w-2xl" data-testid="hero-subline">
              Great Rate Check is a <strong className="text-white font-semibold">conversation engine</strong> for
              relationship-driven pros. Every day you talk with homeowners, buyers, and your sphere —
              we give those conversations a natural next step, backed by licensed mortgage professionals.
            </p>
          </motion.div>

          {/* 3 value cards */}
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.12 }}
            className="mt-9 grid grid-cols-1 sm:grid-cols-3 gap-3"
            data-testid="hero-value-cards"
          >
            {[
              {
                icon: Megaphone,
                title: "What you get",
                body: "Approved messaging, ready-to-use marketing assets, training, and a clean mortgage handoff system — no licensing required on your end.",
              },
              {
                icon: MessagesSquare,
                title: "What you do",
                body: "Keep having the conversations you're already having. When a rate or mortgage topic comes up, you have a simple, professional next step.",
              },
              {
                icon: TrendingUp,
                title: "Why it works",
                body: "You stay the trusted relationship. Licensed pros handle the mortgage side. Your sphere gets real value — and you build a repeatable engine.",
              },
            ].map(({ icon: Icon, title, body }) => (
              <div
                key={title}
                className="rounded-2xl border border-white/10 bg-white/[0.05] backdrop-blur-sm p-5 transition-colors hover:bg-white/[0.08]"
              >
                <div className="w-9 h-9 rounded-xl bg-[#1F8F8B]/25 border border-[#1F8F8B]/30 flex items-center justify-center">
                  <Icon className="w-4.5 h-4.5 text-[#A6D24A]" size={18} />
                </div>
                <div className="mt-3 text-sm font-semibold text-white">{title}</div>
                <p className="mt-1.5 text-[13px] leading-relaxed text-white/70">{body}</p>
              </div>
            ))}
          </motion.div>

          {/* How it works strip */}
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.22 }}
            className="mt-6 rounded-2xl border border-white/10 bg-[#08192F]/60 p-5 sm:p-6"
            data-testid="hero-how-it-works"
          >
            <div className="text-[11px] uppercase tracking-[0.18em] text-[#1F8F8B] font-semibold">How it works</div>
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-2">
              {[
                { n: "1", icon: MessagesSquare, t: "Talk", d: "Bring rate checks into everyday conversations with your sphere." },
                { n: "2", icon: Handshake, t: "Hand off", d: "Connect interested people through a simple, compliant handoff." },
                { n: "3", icon: ShieldCheck, t: "We handle the mortgage side", d: "Licensed MLOs take it from there — you stay in the loop." },
              ].map(({ n, icon: Icon, t, d }, i) => (
                <div key={n} className="relative flex items-start gap-3 sm:pr-6">
                  <div className="shrink-0 w-9 h-9 rounded-full bg-[#A6D24A] text-[#08192F] font-bold text-sm flex items-center justify-center">{n}</div>
                  <div>
                    <div className="flex items-center gap-1.5 text-sm font-semibold text-white">
                      <Icon size={14} className="text-[#A6D24A]" /> {t}
                    </div>
                    <p className="mt-1 text-[13px] leading-snug text-white/65">{d}</p>
                  </div>
                  {i < 2 && (
                    <ArrowRight size={16} className="hidden sm:block absolute right-1 top-2.5 text-white/25" />
                  )}
                </div>
              ))}
            </div>
          </motion.div>

          {/* Who we're looking for + CTA */}
          <motion.div
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.32 }}
            className="mt-7 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6"
          >
            <div className="flex items-center gap-2.5 text-[13px] text-white/75">
              <GraduationCap size={16} className="text-[#A6D24A] shrink-0" />
              <span>We're looking for coachable, organized, relationship-driven pros who'll use the system consistently.</span>
            </div>
            <a
              href="#application"
              data-testid="hero-apply-cta"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("application")?.scrollIntoView({ behavior: "smooth" });
              }}
              className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[#A6D24A] hover:bg-[#b7e159] text-[#08192F] font-semibold text-sm px-6 py-3 shadow-lg shadow-[#A6D24A]/20 transition-colors"
            >
              Ready? Apply below <ArrowDown size={15} className="animate-bounce" />
            </a>
          </motion.div>

          <p className="mt-6 text-[11px] text-white/45 italic">
            Completing this application does not guarantee acceptance or any particular level of program support.
          </p>
        </div>
      </header>

      <main id="application" className="max-w-3xl mx-auto px-6 py-10 space-y-8 scroll-mt-6">
        {referringMlo && (
          <div
            data-testid="bdm-apply-referral-banner"
            className="rounded-2xl bg-lime/15 border border-lime/40 text-navy p-4 flex items-center gap-3"
          >
            <div className="shrink-0 w-10 h-10 rounded-full bg-lime/30 flex items-center justify-center">
              <CheckCircle2 size={20} strokeWidth={2.25} className="text-navy" />
            </div>
            <div className="text-sm leading-snug">
              You're applying through <strong>{referringMlo.name}</strong>
              {referringMlo.nmls ? <> (NMLS #{referringMlo.nmls})</> : null}.
              Your application will be tagged to their referral automatically — no need to fill anything extra.
            </div>
          </div>
        )}
        {!referringMlo && !referringSlug && (
          <div
            data-testid="bdm-apply-directory-hint"
            className="rounded-2xl bg-white border border-linen-border text-navy p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3"
          >
            <div className="shrink-0 w-10 h-10 rounded-full bg-teal/10 flex items-center justify-center text-teal">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
            </div>
            <div className="text-sm leading-snug flex-1">
              <strong>Not sure which loan officer to apply under?</strong>{" "}
              Browse the network directory and apply under the officer whose market and style fit yours — your application follows them for life.
            </div>
            <a
              href="/mlos"
              data-testid="bdm-apply-browse-directory"
              className="inline-flex items-center gap-1.5 rounded-full bg-navy hover:bg-[#173a5f] text-white font-medium text-xs px-4 py-2 shrink-0"
            >
              Browse loan officers <ArrowRight size={13} />
            </a>
          </div>
        )}
        <form onSubmit={submit} className="space-y-8" data-testid="bdm-apply-form">

          {/* Section 1 — Contact Information */}
          <Section number={1} title="Contact Information">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Full name" required>
                <TextInput value={f.full_name} onChange={(e) => set("full_name", e.target.value)} data-testid="apply-full-name" required />
              </Field>
              <Field label="Email address" required>
                <TextInput type="email" value={f.email} onChange={(e) => set("email", e.target.value)} data-testid="apply-email" required />
              </Field>
              <Field label="Mobile phone" required>
                <TextInput value={f.mobile_phone} onChange={(e) => set("mobile_phone", e.target.value)} data-testid="apply-phone" required />
              </Field>
              <Field label="Company / brokerage">
                <TextInput value={f.company} onChange={(e) => set("company", e.target.value)} />
              </Field>
              <Field label="City / state">
                <TextInput value={f.city_state} onChange={(e) => set("city_state", e.target.value)} />
              </Field>
              <Field label="Real estate license number" hint="If applicable">
                <TextInput value={f.real_estate_license} onChange={(e) => set("real_estate_license", e.target.value)} />
              </Field>
            </div>
            <Field label="Are you currently affiliated with a Great Rate Check participating MLO or team?">
              <RadioGroup
                name="grc_affiliation"
                value={f.grc_affiliation}
                onChange={(v) => set("grc_affiliation", v)}
                options={[["yes", "Yes"], ["no", "No"], ["not_sure", "Not sure"]]}
                testid="apply-affiliation"
              />
            </Field>
            {f.grc_affiliation === "yes" && (
              <Field label="If yes, who?">
                <TextInput value={f.grc_affiliation_with} onChange={(e) => set("grc_affiliation_with", e.target.value)} />
              </Field>
            )}
            <Field label="Who referred you?" hint="Name of the person, MLO, or team that recommended you apply." required>
              <TextInput
                value={f.referred_by === NO_REFERRER ? "" : f.referred_by}
                onChange={(e) => set("referred_by", e.target.value)}
                disabled={f.referred_by === NO_REFERRER}
                placeholder={f.referred_by === NO_REFERRER ? NO_REFERRER : ""}
                data-testid="apply-referred-by"
                required
              />
              <button
                type="button"
                onClick={() => set("referred_by", f.referred_by === NO_REFERRER ? "" : NO_REFERRER)}
                data-testid="apply-no-referrer"
                className={`mt-2.5 inline-flex items-center gap-2 rounded-full border px-4 py-2 text-xs font-semibold transition-colors ${
                  f.referred_by === NO_REFERRER
                    ? "border-teal bg-teal-light text-teal"
                    : "border-linen-border bg-white text-ink-600 hover:border-teal hover:text-teal"
                }`}
              >
                <span
                  className={`w-3.5 h-3.5 rounded-full border flex items-center justify-center ${
                    f.referred_by === NO_REFERRER ? "bg-teal border-teal" : "border-ink-300"
                  }`}
                >
                  {f.referred_by === NO_REFERRER ? <Check size={9} strokeWidth={4} className="text-white" /> : null}
                </span>
                No one — I found this on my own
              </button>
            </Field>
          </Section>

          {/* Section 2 — Business Profile (9 questions) */}
          <Section number={2} title="Business Profile" subtitle="Nine quick questions to help us understand your business.">
            {/* Q1 — combined experience + production */}
            <NumberedQ
              number={1}
              title="Real Estate Experience & Production"
              hint="How long have you been actively working in real estate, and how many residential transaction sides have you closed in the past 12 months?"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Years in real estate">
                  <Select value={f.q1_years_in_re} onChange={(v) => set("q1_years_in_re", v)} options={OPTS.q1_years} testid="apply-q1-years" />
                </Field>
                <Field label="Sides closed in past 12 months">
                  <Select value={f.q2_transactions_12mo} onChange={(v) => set("q2_transactions_12mo", v)} options={OPTS.q1_txn} testid="apply-q1-txn" />
                </Field>
              </div>
            </NumberedQ>

            <NumberedQ number={2} title="Business Model & Lead Source" hint="Which best describes your current business?">
              <RadioGroup name="q2" value={f.q4_business_type} onChange={(v) => set("q4_business_type", v)} options={OPTS.q2_business} />
            </NumberedQ>

            <NumberedQ number={3} title="Database Size (Sphere of Influence)" hint="Approximately how many people are in your active CRM, database, or sphere?">
              <RadioGroup name="q3" value={f.q5_sphere_size} onChange={(v) => set("q5_sphere_size", v)} options={OPTS.q3_sphere} />
            </NumberedQ>

            <NumberedQ number={4} title="CRM Usage & Organization" hint="How consistently do you use a CRM or organized database system?">
              <RadioGroup name="q4" value={f.q7_crm_frequency} onChange={(v) => set("q7_crm_frequency", v)} options={OPTS.q4_crm} />
            </NumberedQ>

            <NumberedQ number={5} title="Outreach Consistency" hint="How often do you intentionally communicate with your sphere, clients, or prospects?">
              <RadioGroup name="q5" value={f.q9_outreach_frequency} onChange={(v) => set("q9_outreach_frequency", v)} options={OPTS.q5_outreach} />
            </NumberedQ>

            <NumberedQ number={6} title="Monthly Conversation Capacity" hint="How many intentional 1:1 business conversations can you realistically generate per month?">
              <RadioGroup name="q6" value={f.q10_conversations_per_month} onChange={(v) => set("q10_conversations_per_month", v)} options={OPTS.q6_conversations} />
            </NumberedQ>

            <NumberedQ number={7} title="Current Activity Discipline" hint="How would you rate your consistency in prospecting and follow-up?">
              <RadioGroup name="q7" value={f.q12_consistency} onChange={(v) => set("q12_consistency", v)} options={OPTS.q7_discipline} />
            </NumberedQ>

            <NumberedQ number={8} title="Coachability & System Adoption" hint="How willing are you to follow a structured business development system and be coached?">
              <RadioGroup name="q8" value={f.q20_coachability} onChange={(v) => set("q20_coachability", v)} options={OPTS.q8_coach} />
            </NumberedQ>

            <NumberedQ number={9} title="Program Execution Approach" hint="How would you most likely use Great Rate Check?">
              <RadioGroup name="q9" value={f.q14_approach} onChange={(v) => set("q14_approach", v)} options={OPTS.q9_approach} />
            </NumberedQ>
          </Section>

          {/* Section 3 — BDM Agreement (10 Core Commitments) */}
          <Section
            number={3}
            title="Partner Agreement — 10 Core Commitments"
            subtitle="These are the required participation standards for approval and continued access. Please review each and indicate your agreement."
          >
            <CommitmentList
              items={CORE_COMMITMENTS}
              values={f.agreements_section7}
              onChange={(v) => set("agreements_section7", v)}
              testid="apply-commitments"
            />
          </Section>

          {/* Section 4 — Certification */}
          <Section number={4} title="Certification">
            <p className="text-sm text-[#59564C] leading-relaxed">
              By submitting this application, I certify that the information I have provided is
              accurate to the best of my knowledge. I understand that this application is intended to
              help Great Rate Check determine program fit, onboarding needs, and the appropriate level
              of support. Acceptance into the program is not based on any single qualification,
              production level, number of years in business, or database size.
            </p>
            <p className="text-sm text-[#59564C] leading-relaxed">
              Great Rate Check may consider the applicant's overall experience, network, activity,
              professionalism, team affiliation, business-development potential, willingness to be
              coached, and commitment to program requirements.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Applicant name" required hint="Your full legal name">
                <TextInput value={f.applicant_name} onChange={(e) => set("applicant_name", e.target.value)} data-testid="apply-applicant-name" required />
              </Field>
              <Field label="Electronic signature" required hint="Type your full legal name">
                <TextInput value={f.signature} onChange={(e) => set("signature", e.target.value)} data-testid="apply-signature" required />
              </Field>
              <Field label="Date" hint="Today's date">
                <TextInput type="date" value={f.signed_date} onChange={(e) => set("signed_date", e.target.value)} />
              </Field>
            </div>
          </Section>

          {err ? (
            <div className="rounded-2xl bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm" data-testid="apply-error">
              {err}
            </div>
          ) : null}

          <div className="flex items-center justify-end gap-3 pb-16">
            <button
              type="submit"
              disabled={!canSubmit || submitting}
              data-testid="apply-submit"
              className="inline-flex items-center gap-2 rounded-2xl bg-[#0F2A47] hover:bg-[#173a5f] text-white px-6 py-3 shadow disabled:opacity-60"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              Submit application
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <p className="text-[11px] text-[#7A7668] text-center flex items-center justify-center gap-1.5 pb-8">
            <ShieldCheck className="w-3.5 h-3.5" /> Great Rate Check · Your information is handled confidentially.
          </p>
        </form>
      </main>
    </div>
  );
}
